import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase_client';

export default function ResumePayment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'completed' | 'expired' | 'invalid' | 'failed' | 'other'>('other');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resumePayment = useCallback(async () => {
    if (!token) {
      setError('No token provided');
      setErrorType('invalid');
      setLoading(false);
      return;
    }

    try {
      // Call the resume-payment edge function
      const { data, error: functionError } = await supabase.functions.invoke('resume-payment', {
        body: { token: token }
      });

      // Handle function errors - try to extract error details from response
      if (functionError) {
        // Try to parse error details if available
        // Supabase might include error details in the error object
        const errorDetails = (functionError as any)?.context || (functionError as any)?.details;
        
        // Check if we have data despite the error (sometimes Supabase returns data even with errors)
        if (data?.error) {
          // Check if payment is already completed
          if (data.purchaseStatus === 'paid' || data.error === 'Payment already completed') {
            setErrorType('completed');
            setErrorMessage(data.message || 'This payment has already been completed successfully.');
            setError(data.error);
            setLoading(false);
            return;
          }
        }
        
        // If error details contain payment completed info, handle it
        if (errorDetails && typeof errorDetails === 'object') {
          if (errorDetails.purchaseStatus === 'paid' || errorDetails.error === 'Payment already completed') {
            setErrorType('completed');
            setErrorMessage(errorDetails.message || 'This payment has already been completed successfully.');
            setError(errorDetails.error || 'Payment already completed');
            setLoading(false);
            return;
          }
        }
        
        // Otherwise, throw the error
        throw new Error(functionError.message || 'Failed to resume payment');
      }

      if (data?.error) {
        // Check if payment is already completed
        if (data.purchaseStatus === 'paid' || data.error === 'Payment already completed') {
          setErrorType('completed');
          setErrorMessage(data.message || 'This payment has already been completed successfully.');
        } else if (data.purchaseStatus === 'failed' || data.error === 'Payment already failed') {
          setErrorType('failed'); 
          setErrorMessage(data.message || 'This payment has already been marked as failed. Please initiate a new payment if you wish to complete the purchase.');
        } else if (data.error.includes('expired')) {
          setErrorType('expired');
          setErrorMessage(data.error);
        } else if (data.error.includes('Invalid') || data.error.includes('Token')) {
          setErrorType('invalid');
          setErrorMessage(data.error);
        } else {
          setErrorType('other');
          setErrorMessage(data.message || data.error);
        }
        setError(data.error);
        setLoading(false);
        return;
      }

      // If we get a payment URL, redirect to it
      if (data?.paymentUrl) {
        window.location.href = data.paymentUrl;
        return;
      }

      // If no URL, show error
      setError('Failed to get payment URL');
      setErrorType('other');
      setLoading(false);

    } catch (err: any) {
      console.error('Error resuming payment:', err);
      setError(err.message || 'Failed to resume payment');
      setErrorType('other');
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    resumePayment();
  }, [resumePayment]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to payment page...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Show success-style UI for completed payments
    if (errorType === 'completed') {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold mb-2 text-gray-900">Payment Already Completed</h2>
            <p className="text-gray-600 mb-4">
              {errorMessage || 'This payment has already been completed successfully. You do not need to resume it.'}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      );
    }

    // Show failure UI for failed payments
    if (errorType === 'failed') {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
            <div className="text-red-500 text-6xl mb-4">✕</div>
            <h2 className="text-2xl font-bold mb-2 text-gray-900">Payment Failed</h2>
            <p className="text-gray-600 mb-4">
              {errorMessage ? (
                <span dangerouslySetInnerHTML={{
                  __html: errorMessage.replace(
                    /(\bfailed\b)/gi,
                    '<strong class="font-bold text-red-600">$1</strong>'
                  )
                }} />
              ) : (
                <>This payment has already been marked as <strong className="font-bold text-red-600">failed</strong>. Please initiate a new payment if you wish to complete the purchase.</>
              )}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      );
    }

    // Show error UI for other cases
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <div className="text-red-500 text-6xl mb-4">✕</div>
          <h2 className="text-2xl font-bold mb-2 text-gray-900">Unable to Resume Payment</h2>
          <p className="text-gray-600 mb-4">
            {errorMessage || error}
          </p>
          {errorType === 'expired' && (
            <p className="text-sm text-gray-500 mb-4">
              This payment link has expired. Please initiate a new payment from your dashboard.
            </p>
          )}
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return null;
}